import React, { useState, useEffect, useContext, useCallback } from 'react';
import withAppContext from "tbk-components/src/hooks/withAppContext";
import CareersIndexHeroArea from '../CareersIndexHeroArea';
import {ICareersFilterProps} from "../CareerCardGroup";
import {useHashAsState} from "tbk-components/src/hooks";
import Spacer from "../../common/Spacer";
import CareerCardGroup from '../CareerCardGroup';
import {ICareerCardProps} from "../CareerCard";
import qs from 'qs';
import { debounce } from 'lodash';
import { handleGtmTags } from '../../../helpers/gtm';

export interface ICareersIndexProps {
	heading: string;
	tagline: string;
	searchPlaceholder: string;
	perPage: number;
	loadMoreButtonText: string;
	remoteCheckboxText?: string;
	noResultsText: string;
	defaultFilters?: ICareersFilterProps[]
}

const CareersIndex: React.FC<ICareersIndexProps> = withAppContext(({
	heading,
	tagline,
	searchPlaceholder,
	perPage=5,
	loadMoreButtonText,
	remoteCheckboxText,
	noResultsText,
	defaultFilters,
	context,
}) => {
	const { Api } = useContext(context);
	const [ filters, setFilters ] = useState<ICareersFilterProps[]>(null);
	const [ careers, setCareers ] = useState<{total:number,cards:ICareerCardProps[],tagline:string}>(null);
	const [ loadingMore, setLoadingMore ] = useState(0);
	const [ busy, setBusy ] = useState(true);

	const dynamicParameters = {};
	(filters || []).forEach( filter => {
		dynamicParameters[filter.taxonomy] = 'string';
	});

	const [ state, normalizedState, updateState ] = useHashAsState({
		parameters: {
			currentPage : 'number',
			search: 'string',
			remote: 'number',
			...dynamicParameters
		},
		defaults: {
			currentPage : 1,
		},
	});

	useEffect(() => {
		if ( defaultFilters ) {
			setFilters(defaultFilters);
		} else {
			Api.getCareerIndexFilters().then( filters => {
				setFilters(filters);
			});
		}
	}, [defaultFilters] );

	// Fetch careers from the API, with a debounce to prevent multiple
	// API calls (e.g. on each keystroke when somebody is typing.)
	const fetchPostsFromApi = useCallback(
		debounce((args: any) => {
			setBusy(true);
			Api.getFilteredJobs(args).then( results => {
				setCareers(careers => {
					return {
						...results,
						cards: loadingMore ? [...careers.cards,...results.cards] : results.cards,
					}
				});
				handleGtmTags( results );
			}).finally(() => {
				setBusy(false);
			});
		}, 250),
		[loadingMore]
	);


	useEffect(() => {
		if ( ! filters ) {
			return;
		}
		setBusy(true);
		const state = qs.parse(normalizedState.replace(/^#!/,''));
		fetchPostsFromApi({...state,loadingMore,perPage});
	},[filters,normalizedState,loadingMore]);

	const onChangeFilter = (taxonomy:string,value:string) => {
		updateState({[taxonomy]:value,currentPage:1});
		setLoadingMore(0);
	}

	return <>
		<CareersIndexHeroArea heading={heading}
									filters={filters}
									filterValues={state}
									searchPlaceholder={searchPlaceholder}
									remoteCheckboxText={remoteCheckboxText}
									onChangeFilter={onChangeFilter}
		/>
		<Spacer size={'Small'} />
		<CareerCardGroup showFilter={false}
							   showResults={true}
							   cardSummaryHeading={careers?.tagline}
							   loadMoreButtonText={loadMoreButtonText}
							   noResultsText={noResultsText}
							   calianCareerCards={careers?.cards}
							   totalResults={careers?.total}
							   cardsToShow={perPage * parseInt(state.currentPage,10)}
							   onShowMore={() => {
								setLoadingMore(1);
								updateState({currentPage:parseInt(state.currentPage,10)+1});
							   }}
							   onClickCareerTerm={onChangeFilter}
							   busy={busy && !loadingMore}
							   loadingMore={busy && !!loadingMore}

		/>
		<Spacer size={'Large on Desktop'} />

	</>;
});

export default CareersIndex;

